/**
 * Finance · Financial Risks.
 */
import { useMemo, useState } from "react";
import {
  ShieldAlert, Sparkles, Coins, Users, TrendingDown, Wallet, ClipboardCheck, Percent,
} from "lucide-react";
import { Card, SectionHeader, TextInput, Button, SeverityBadge, ConfidenceBar } from "../../components/lib";
import { TabBar, Drawer, Field } from "../../components/lib.overlay";
import { FIN_RISKS, FinRisk, FinRiskCategory } from "../../mocks/financeData";

const CATEGORY_META: Record<FinRiskCategory, { icon: any; label: string; tint: string; fg: string }> = {
  fx:            { icon: Coins,           label: "FX",             tint: "var(--ph-warning-tint)", fg: "var(--ph-warning)"       },
  receivables:   { icon: Wallet,          label: "Receivables",    tint: "var(--ph-danger-tint)",  fg: "var(--ph-danger)"        },
  runway:        { icon: TrendingDown,    label: "Runway",         tint: "var(--ph-success-tint)", fg: "var(--ph-success)"       },
  concentration: { icon: Users,           label: "Concentration",  tint: "var(--ph-brand-tint)",   fg: "var(--ph-brand-primary)" },
  margin:        { icon: Percent,         label: "Margin",         tint: "var(--ph-warning-tint)", fg: "var(--ph-warning)"       },
  compliance:    { icon: ClipboardCheck,  label: "Compliance",     tint: "var(--ph-ai-tint)",      fg: "var(--ph-ai)"            },
};

const STATUS_META: Record<FinRisk["status"], { bg: string; fg: string; label: string }> = {
  open:       { bg: "var(--ph-danger-tint)",  fg: "var(--ph-danger)",  label: "Open"       },
  monitoring: { bg: "var(--ph-warning-tint)", fg: "var(--ph-warning)", label: "Monitoring" },
  mitigated:  { bg: "var(--ph-success-tint)", fg: "var(--ph-success)", label: "Mitigated"  },
};

export default function Risks() {
  const [tab, setTab] = useState<FinRisk["status"] | "all">("open");
  const [category, setCategory] = useState<FinRiskCategory | "all">("all");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState<FinRisk | null>(null);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: FIN_RISKS.length };
    FIN_RISKS.forEach((r) => { c[r.status] = (c[r.status] || 0) + 1; });
    return c;
  }, []);

  const catCounts = useMemo(() => {
    const c: Record<string, number> = { all: FIN_RISKS.length };
    FIN_RISKS.forEach((r) => { c[r.category] = (c[r.category] || 0) + 1; });
    return c;
  }, []);

  const filtered = useMemo(
    () => FIN_RISKS.filter((r) =>
      (tab === "all" || r.status === tab) &&
      (category === "all" || r.category === category) &&
      (!q || r.title.toLowerCase().includes(q.toLowerCase()) || r.note.toLowerCase().includes(q.toLowerCase())),
    ),
    [tab, category, q],
  );

  const totalExposure = FIN_RISKS
    .filter((r) => r.status !== "mitigated")
    .reduce((n, r) => n + r.exposureEGP, 0);

  return (
    <div className="space-y-6" data-testid="fn-risks">
      <Card testId="fn-risk-summary">
        <SectionHeader
          eyebrow="Register"
          title="Financial risk posture"
          action={<ShieldAlert size={14} style={{ color: "var(--ph-ink-muted)" }} />}
        />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Open</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-danger)" }}>{counts.open || 0}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Monitoring</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-warning)" }}>{counts.monitoring || 0}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Mitigated</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-success)" }}>{counts.mitigated || 0}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--ph-ink-muted)" }}>Total exposure</div>
            <div className="text-xl font-semibold tabular-nums mt-1" style={{ color: "var(--ph-ink-strong)" }}>
              EGP {(totalExposure / 1000).toFixed(0)}K
            </div>
          </div>
        </div>
      </Card>

      <Card testId="fn-risk-filters">
        <div className="flex items-center gap-3">
          <div className="flex-1">
            <TextInput
              value={q}
              onChange={setQ}
              placeholder="Search financial risks…"
              testId="fn-risk-search"
            />
          </div>
          <Button testId="fn-risk-new">+ Log risk</Button>
        </div>
        <div className="mt-4 flex items-center gap-3 flex-wrap">
          <TabBar<FinRisk["status"] | "all">
            testId="fn-risk-tabs"
            active={tab}
            onChange={setTab}
            tabs={[
              { key: "open",       label: "Open",       count: counts.open       || 0 },
              { key: "monitoring", label: "Monitoring", count: counts.monitoring || 0 },
              { key: "mitigated",  label: "Mitigated",  count: counts.mitigated  || 0 },
              { key: "all",        label: "All",        count: counts.all             },
            ]}
          />
          <TabBar<FinRiskCategory | "all">
            testId="fn-risk-cat"
            active={category}
            onChange={setCategory}
            tabs={[
              { key: "all",           label: "All"                                              },
              { key: "fx",            label: "FX",            count: catCounts.fx            || 0 },
              { key: "receivables",   label: "Receivables",   count: catCounts.receivables   || 0 },
              { key: "runway",        label: "Runway",        count: catCounts.runway        || 0 },
              { key: "concentration", label: "Concentration", count: catCounts.concentration || 0 },
              { key: "margin",        label: "Margin",        count: catCounts.margin        || 0 },
              { key: "compliance",    label: "Compliance",    count: catCounts.compliance    || 0 },
            ]}
          />
        </div>
      </Card>

      <Card padded={false} testId="fn-risk-list-card">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Register" title={`${filtered.length} risk${filtered.length === 1 ? "" : "s"}`} />
        </div>
        <ul className="divide-y" style={{ borderColor: "var(--ph-border-subtle)" }} data-testid="fn-risk-list">
          {filtered.map((r) => {
            const cat = CATEGORY_META[r.category];
            const Icon = cat.icon;
            const st = STATUS_META[r.status];
            return (
              <li key={r.id} data-testid={`fn-risk-${r.id}`}
                className="p-4 flex items-center gap-3 cursor-pointer hover:bg-slate-50"
                onClick={() => setSelected(r)}>
                <span className="inline-flex items-center justify-center flex-shrink-0" style={{
                  width: 28, height: 28, borderRadius: "999px",
                  background: cat.tint, color: cat.fg,
                }}>
                  <Icon size={13} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-sm font-semibold" style={{ color: "var(--ph-ink-strong)" }}>{r.title}</span>
                    <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: cat.tint, color: cat.fg, borderRadius: "var(--ph-radius-sm)" }}>
                      {cat.label}
                    </span>
                    <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: st.bg, color: st.fg, borderRadius: "var(--ph-radius-sm)" }}>
                      {st.label}
                    </span>
                  </div>
                  <div className="text-[11px] mt-0.5" style={{ color: "var(--ph-ink-muted)" }}>
                    Exposure EGP {r.exposureEGP.toLocaleString()} · <span className="inline-flex items-center gap-1" style={{ color: "var(--ph-ai)" }}><Sparkles size={9} /> AI {Math.round(r.aiConfidence * 100)}%</span>
                  </div>
                </div>
                <SeverityBadge severity={r.severity} />
              </li>
            );
          })}
        </ul>
      </Card>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        title="Financial risk"
        testId="fn-risk-drawer"
        width={480}
      >
        {selected && (
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em]" style={{ color: CATEGORY_META[selected.category].fg }}>
              {CATEGORY_META[selected.category].label}
            </div>
            <div style={{ fontFamily: "var(--ph-display-font)", fontSize: "1.2rem", color: "var(--ph-ink-strong)" }} className="mt-1 mb-3">
              {selected.title}
            </div>
            <Field label="Status" inline>
              <span
                className="px-2 py-0.5 text-[10px] font-semibold uppercase"
                style={{
                  letterSpacing: "0.14em",
                  background: STATUS_META[selected.status].bg,
                  color: STATUS_META[selected.status].fg,
                  borderRadius: "var(--ph-radius-sm)",
                }}
              >
                {STATUS_META[selected.status].label}
              </span>
            </Field>
            <Field label="Severity" inline><SeverityBadge severity={selected.severity} /></Field>
            <Field label="Exposure" inline>
              <span className="text-sm font-semibold tabular-nums" style={{ color: "var(--ph-ink-strong)" }}>
                EGP {selected.exposureEGP.toLocaleString()}
              </span>
            </Field>
            <Field label="AI confidence" inline>
              <div style={{ width: 120 }}>
                <div className="text-[11px] mb-1 tabular-nums" style={{ color: "var(--ph-ai)" }}>{Math.round(selected.aiConfidence * 100)}%</div>
                <ConfidenceBar value={selected.aiConfidence} />
              </div>
            </Field>
            <div className="pt-3">
              <div className="text-[10px] uppercase tracking-[0.14em] mb-1.5" style={{ color: "var(--ph-ink-muted)" }}>Mitigation note</div>
              <div
                className="text-sm p-3"
                style={{
                  background: "var(--ph-surface)",
                  borderRadius: "var(--ph-radius-md)",
                  color: "var(--ph-ink-default)",
                }}
              >
                {selected.note}
              </div>
            </div>
            <div className="pt-4 flex justify-end gap-2">
              <Button variant="secondary" testId="fn-risk-monitor">Move to monitoring</Button>
              <Button testId="fn-risk-mitigate">Mark mitigated</Button>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
}
